import React, { useEffect, useMemo, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { useCallCenter } from '../providers/CallCenterProvider';
import { AppConfig } from '@r6digital/rapidstor-types/react/entities/Journey';
import { CallCenterLocation } from '../util/Corporations';
import { JBL_WIDGET_BASE_URL, JBL_WIDGET_DEV_MODE } from '../api/config';
import PricingCalculator from './PricingCalculator';

export const BaseConfig: AppConfig = {
    pages: {
        storageType: true,
        unitListing: true,
        bookUnit: 'standard',
    },
    journey: {
        locationCode: 'TEST',
        corpCode: 'CRUK',
        apiToken: '0zbo0yM8TFiiM0dmowS8f46h2M4WmClK',
        corpToken:
            'adfe4749288ea6045aafabadc8aab35a803ef666844261ac4149a6f7fde9e2e45f116e4aff94d16aedf2c37aed410331',
        themeColor: '#000',
    },
};

export default function View() {
    /** Hooks */
    const [cookies] = useCookies();
    const {
        configuration,
        locations,
        selectedLocationCode,
        savedAgentName,
        savedAgentComments,
    } = useCallCenter();

    /** Refs */
    const standardUnitListingRef = useRef<HTMLDivElement>(null);

    /** Constants */
    const locationCode = selectedLocationCode;

    const selectedLocation = useMemo(() => {
        return locations.find((loc) => loc.code === locationCode) || null;
    }, [locations, locationCode]);

    // The JBL widget reads agent details from the saved cookie, not the live
    // input state — block interaction with it until that cookie is actually
    // populated, so a reservation can't go out untracked.
    const requiresAgentDetails =
        configuration?.bookingMode === 'jbl' &&
        !(savedAgentName.trim() && savedAgentComments.trim());

    /**
     * Initialize the standard unit listing
     *
     * @returns {void}
     */
    const makeStandardUnitListing = (): void => {
        if (
            !standardUnitListingRef.current ||
            window.rapidstor_options ||
            !selectedLocation ||
            !configuration
        ) {
            return;
        }

        const child = document.createElement('div');
        child.setAttribute('id', 'rapidstor-app');
        standardUnitListingRef.current.replaceChildren();
        standardUnitListingRef.current.appendChild(child);

        const o = {
            mode: 'unit-listing',
            token: {
                corp: configuration?.appConfig.journey.corpToken,
                site: selectedLocation.locationCode,
            },
            url:
                import.meta.env.VITE_RAPIDSTOR_BASE_URL ??
                'https://prod.rapidstorapp.com',
            parent_element_id: 'rapidstor-app',
            mount_element: '#rapidstor-app',
            script_path:
                '/public/application/scripts/publicPanel/bootstrapper/bootstrapper.js',
            ignore_template: false,
            standalone: false,
            loaded: false,
            loading: true,
            load_scripts: !window.rapidstor_options,
        };
        const t = document.getElementById(o.parent_element_id);
        if (!t) {
            return;
        }
        const s = document.createElement('script');
        const e = Math.round(new Date().getTime() / 1000);
        window.rapidstor_options = o;
        s.src = o.url + o.script_path + '?epoch=' + e;
        console.log("rsbsl: loading '" + s.src + "'...");
        s.type = 'text/javascript';
        s.async = true;
        s.onload = function () {
            window.rapidstor_options.loaded = true;
            console.log("rsbsl: loaded '" + s.src + "'.");
        };
        t.appendChild(s);
    };

    /**
     * Initialize the JBL
     *
     * @param selectedLocation {CallCenterLocation}
     *
     * @returns {Promise<void>}
     */
    const makeJbl = async (selectedLocation: CallCenterLocation): Promise<void> => {
        const contents = await fetch(`${JBL_WIDGET_BASE_URL}/`);
        const result = await contents.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(result, `text/html`);
        const script = doc.querySelector(`head script`);
        const st = document.createElement(`script`);
        const p = document.getElementById(`rapidstor-v2-frontend`);
        st.src = `${JBL_WIDGET_BASE_URL}` + script?.getAttribute(`src`);
        st.setAttribute(`type`, `module`);
        st.setAttribute(`crossorigin`, 'true');
        p?.appendChild(st);
        const container = document.getElementById(`rapidstor-v2-frontend`);
        const config = {
            ...BaseConfig,
            journey: {
                ...configuration?.appConfig.journey,
                locationCode: selectedLocation.sitelinkCode,
                dev: JBL_WIDGET_DEV_MODE,
                agentDetails: {
                    agentName: cookies.agentDetails
                        ? cookies.agentDetails.agentName
                        : undefined,
                    agentComment: cookies.agentDetails
                        ? cookies.agentDetails.agentComments
                        : undefined,
                },
            },
        };

        if (selectedLocation.unitTypeRegex) {
            config.journey.unitTypeRegex = selectedLocation.unitTypeRegex;
        }

        container?.setAttribute('data-appConfig', JSON.stringify(config));
    };

    /**
     * Handles the initialization of the selected configuration/location and its corresponding bookingMode
     */
    useEffect(() => {
        if (!locationCode) {
            console.error(`No config found for locationCode: ${locationCode}`);
            return;
        }

        if (!selectedLocation) {
            console.error(`No location found for locationCode: ${locationCode}`);
            return;
        }

        const initApp = async () => {
            if (configuration?.bookingMode === 'jbl') {
                await makeJbl(selectedLocation);
            } else if (configuration?.bookingMode === 'standard-unit-listing') {
                makeStandardUnitListing();
            }
        };

        initApp();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies.agentDetails, selectedLocation, locationCode]);

    if (configuration?.bookingMode === 'pricing-calculator') {
        return selectedLocation ? (
            <PricingCalculator />
        ) : (
            <span className='my-12 text-3xl font-bold items-center flex justify-center'>
                Select a location from the dropdown to begin
            </span>
        );
    }

    return (
        <div className='relative'>
            {selectedLocation ? (
                <>
                    <div
                        id='rapidstor-v2-frontend'
                        className='rapidstor-v2-frontend'
                        style={{
                            minHeight:
                                configuration?.bookingMode === 'jbl' ? '100vh' : 'auto',
                        }}
                        {...(requiresAgentDetails ? { inert: '' } : {})}
                    ></div>
                    <div
                        id='rapidstor-app'
                        ref={standardUnitListingRef}
                        {...(requiresAgentDetails ? { inert: '' } : {})}
                    ></div>

                    {requiresAgentDetails && (
                        <div className='absolute inset-0 z-30 flex items-start justify-center bg-white/90 pt-24 backdrop-blur-sm'>
                            <div className='mx-4 max-w-md rounded-lg border-2 border-solid border-amber-400 bg-amber-50 p-6 text-center shadow-lg'>
                                <p className='text-lg font-semibold text-amber-800'>
                                    Please enter your Agent Name and Source above, then
                                    click Save to continue.
                                </p>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <span className='my-12 text-3xl font-bold items-center flex justify-center'>
                    Select a location from the dropdown to begin
                </span>
            )}
        </div>
    );
}
